import React, { useState } from "react";

import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";

import { DefaultLoader, PageTitle } from "@rdcs/dap-front-library";

import { trySinglePatient } from "../../../actions/patients";
import { try30QuestionnairePatient } from "../../../actions/questionnaire";
import Questions from "../../../actions/questions";

import Form from "../../../components/BreakScoreQuestionnaire/ForExistingPatient/Form";
import MultiplePastQuestionnaires from "../../../components/BreakScoreQuestionnaire/MultiplePastQuestionnaires";
import NoPastQuestionnaire from "../../../components/BreakScoreQuestionnaire/NoPastQuestionnaire";
import SinglePastQuestionnaire from "../../../components/BreakScoreQuestionnaire/SinglePastQuestionnaire";
import PatientNavLayout from "../../../components/Patients/PatientNavLayout";

import { formatDate } from "../../../utils/utils";

const { tryFetchBreakScoreNoInsulinQuestions } = Questions;

const PatientsNavBreakScoreScreen = ({
    breakScoreNoInsulinQuestions,
    breakScoreQuestions,
    try30QuestionnairePatient,
    trySinglePatient,
}) => {
    const { id } = useParams();
    const patientId = React.useMemo(() => parseInt(id), [id]);

    const [isLoading, setIsLoading] = React.useState(true);
    const [questionnaires, setQuestionnaires] = React.useState([]);
    const [isInsulinoTreated, setIsInsulinoTreated] = React.useState(false);
    const [questionnaireList, setQuestionnaireList] = React.useState([]);
    const [fillNewQuestionnaire, setFillNewQuestionnaire] = useState(false);

    React.useEffect(() => {
        if (fillNewQuestionnaire) return;

        setIsLoading(true);
        const dataNotInsulinoTreated = {
            type: "break-score-no-insulin",
            patientId,
        };

        const dataInsulinoTreated = {
            type: "break-score",
            patientId,
        };

        //A patient is supposed to have insulino OR non-insulino questionnaires, but NOT both
        Promise.allSettled([
            trySinglePatient(patientId),
            try30QuestionnairePatient(dataNotInsulinoTreated).then((resNotInsulino) => {
                if (resNotInsulino["hydra:member"]) {
                    setQuestionnaires(resNotInsulino["hydra:member"]);
                    setIsInsulinoTreated(false);
                }
            }),
            try30QuestionnairePatient(dataInsulinoTreated).then((resInsulino) => {
                if (resInsulino["hydra:member"]) {
                    setIsInsulinoTreated(true);
                    setQuestionnaires(resInsulino["hydra:member"]);
                }
            }),
        ]).then(() => {
            setIsLoading(false);
        });
    }, [patientId, fillNewQuestionnaire]);

    React.useEffect(() => {
        let list = [];

        for (let i = 0; i < questionnaires.length; i++) {
            list.push({
                date: formatDate(questionnaires[i].createdAt),
                score: Math.round(questionnaires[i].score),
            });
        }
        setQuestionnaireList(list);
    }, [questionnaires]);

    if (fillNewQuestionnaire)
        return (
            <PageTitle title="Timkl - Questionnaire de rupture">
                <PatientNavLayout patientId={patientId}>
                    <Form setFillNewQuestionnaire={setFillNewQuestionnaire} />
                </PatientNavLayout>
            </PageTitle>
        );

    return (
        <PageTitle title="Timkl - Questionnaire de rupture">
            <PatientNavLayout patientId={patientId}>
                {isLoading ? (
                    <DefaultLoader color="#CCC" />
                ) : questionnaires.length === 0 && !isLoading ? (
                    <NoPastQuestionnaire setFillNewQuestionnaire={setFillNewQuestionnaire} />
                ) : questionnaires.length === 1 ? (
                    <SinglePastQuestionnaire
                        breakScoreNoInsulinQuestions={breakScoreNoInsulinQuestions}
                        breakScoreQuestions={breakScoreQuestions}
                        isInsulinoTreated={isInsulinoTreated}
                        questionnaire={questionnaires[0]}
                        setFillNewQuestionnaire={setFillNewQuestionnaire}
                    />
                ) : (
                    <MultiplePastQuestionnaires
                        breakScoreNoInsulinQuestions={breakScoreNoInsulinQuestions}
                        breakScoreQuestions={breakScoreQuestions}
                        isInsulinoTreated={isInsulinoTreated}
                        questionnaireList={questionnaireList}
                        questionnaires={questionnaires}
                        setFillNewQuestionnaire={setFillNewQuestionnaire}
                    />
                )}
            </PatientNavLayout>
        </PageTitle>
    );
};

PatientsNavBreakScoreScreen.propTypes = {
    breakScoreNoInsulinQuestions: PropTypes.arrayOf(PropTypes.object),
    breakScoreQuestions: PropTypes.arrayOf(PropTypes.object),
    try30QuestionnairePatient: PropTypes.func.isRequired,
    trySinglePatient: PropTypes.func.isRequired,
};

const mapStateToProps = ({ questions: { breakScoreQuestions, breakScoreNoInsulinQuestions } }) => ({
    breakScoreQuestions,
    breakScoreNoInsulinQuestions,
});

const mapDispatchToProps = {
    try30QuestionnairePatient,
    tryFetchBreakScoreNoInsulinQuestions,
    trySinglePatient,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientsNavBreakScoreScreen);
